/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
//module Shumway.AVM1.Lib {
import { alCoerceNumber, alDefineObjectProperties } from '../runtime';
import { ColorTransform, ColorUtils } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
function defaultTo(v, defaultValue) {
    return v === undefined ? defaultValue : v;
}
export function fromAVM1Object(v) {
    var context = v.context;
    if (!(v instanceof AVM1Object)) {
        return new AVM1ColorTransform(context, 1, 1, 1, 1, 0, 0, 0, 0);
    }
    var off_a = v.alGet('alphaOffset');
    if (off_a == null) {
        off_a = v.alGet('ab');
    }
    var mul_a = v.alGet('alphaMultiplier');
    if (mul_a == null) {
        mul_a = v.alGet('aa');
        if (mul_a != null) {
            mul_a = mul_a / 100;
        }
    }
    var off_r = v.alGet('redOffset');
    if (off_r == null) {
        off_r = v.alGet('rb');
    }
    var mul_r = v.alGet('redMultiplier');
    if (mul_r == null) {
        mul_r = v.alGet('ra');
        if (mul_r != null) {
            mul_r = mul_r / 100;
        }
    }
    var off_g = v.alGet('greenOffset');
    if (off_g == null) {
        off_g = v.alGet('gb');
    }
    var mul_g = v.alGet('greenMultiplier');
    if (mul_g == null) {
        mul_g = v.alGet('ga');
        if (mul_g != null) {
            mul_g = mul_g / 100;
        }
    }
    var off_b = v.alGet('blueOffset');
    if (off_b == null) {
        off_b = v.alGet('bb');
    }
    var mul_b = v.alGet('blueMultiplier');
    if (mul_b == null) {
        mul_b = v.alGet('ba');
        if (mul_b != null) {
            mul_b = mul_b / 100;
        }
    }
    return new AVM1ColorTransform(context, alCoerceNumber(context, defaultTo(mul_r, 1)), alCoerceNumber(context, defaultTo(mul_g, 1)), alCoerceNumber(context, defaultTo(mul_b, 1)), alCoerceNumber(context, defaultTo(mul_a, 1)), alCoerceNumber(context, defaultTo(off_r, 0)), alCoerceNumber(context, defaultTo(off_g, 0)), alCoerceNumber(context, defaultTo(off_b, 0)), alCoerceNumber(context, defaultTo(off_a, 0)));
}
export function toAwayColorTransform(v) {
    var context = v.context;
    if (!(v instanceof AVM1ColorTransform)) {
        return new ColorTransform(1, 1, 1, 1, 0, 0, 0, 0);
    }
    return new ColorTransform(alCoerceNumber(context, defaultTo(v._rawData[0], 1)), alCoerceNumber(context, defaultTo(v._rawData[1], 1)), alCoerceNumber(context, defaultTo(v._rawData[2], 1)), alCoerceNumber(context, defaultTo(v._rawData[3], 1)), alCoerceNumber(context, defaultTo(v._rawData[4], 0)), alCoerceNumber(context, defaultTo(v._rawData[5], 0)), alCoerceNumber(context, defaultTo(v._rawData[6], 0)), alCoerceNumber(context, defaultTo(v._rawData[7], 0)));
}
export function copyAS3ColorTransform(t, v) {
    v._rawData[0] = t.redMultiplier;
    v._rawData[1] = t.greenMultiplier;
    v._rawData[2] = t.blueMultiplier;
    v._rawData[3] = t.alphaMultiplier;
    v._rawData[4] = t.redOffset;
    v._rawData[5] = t.greenOffset;
    v._rawData[6] = t.blueOffset;
    v._rawData[7] = t.alphaOffset;
}
var AVM1ColorTransform = /** @class */ (function (_super) {
    __extends(AVM1ColorTransform, _super);
    function AVM1ColorTransform(context, redMultiplier, greenMultiplier, blueMultiplier, alphaMultiplier, redOffset, greenOffset, blueOffset, alphaOffset) {
        if (redMultiplier === void 0) { redMultiplier = 1; }
        if (greenMultiplier === void 0) { greenMultiplier = 1; }
        if (blueMultiplier === void 0) { blueMultiplier = 1; }
        if (alphaMultiplier === void 0) { alphaMultiplier = 1; }
        if (redOffset === void 0) { redOffset = 0; }
        if (greenOffset === void 0) { greenOffset = 0; }
        if (blueOffset === void 0) { blueOffset = 0; }
        if (alphaOffset === void 0) { alphaOffset = 0; }
        var _this = _super.call(this, context) || this;
        _this._rawData = [redMultiplier, greenMultiplier, blueMultiplier, alphaMultiplier,
            redOffset, greenOffset, blueOffset, alphaOffset];
        _this.alPrototype = context.globals.ColorTransform.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            rgb: {
                get: _this.getRgb,
                set: _this.setRgb
            },
            ra: {
                get: _this.getRM,
                set: _this.setRM
            },
            ga: {
                get: _this.getGM,
                set: _this.setGM
            },
            ba: {
                get: _this.getBM,
                set: _this.setBM
            },
            aa: {
                get: _this.getAM,
                set: _this.setAM
            },
            rb: {
                get: _this.getRO,
                set: _this.setRO
            },
            gb: {
                get: _this.getGO,
                set: _this.setGO
            },
            bb: {
                get: _this.getBO,
                set: _this.setBO
            },
            ab: {
                get: _this.getAO,
                set: _this.setAO
            },
            redMultiplier: {
                get: _this.getRedMultiplier,
                set: _this.setRedMultiplier
            },
            greenMultiplier: {
                get: _this.getGreenMultiplier,
                set: _this.setGreenMultiplier
            },
            blueMultiplier: {
                get: _this.getBlueMultiplier,
                set: _this.setBlueMultiplier
            },
            alphaMultiplier: {
                get: _this.getAlphaMultiplier,
                set: _this.setAlphaMultiplier
            },
            redOffset: {
                get: _this.getRedOffset,
                set: _this.setRedOffset
            },
            greenOffset: {
                get: _this.getGreenOffset,
                set: _this.setGreenOffset
            },
            blueOffset: {
                get: _this.getBlueOffset,
                set: _this.setBlueOffset
            },
            alphaOffset: {
                get: _this.getAlphaOffset,
                set: _this.setAlphaOffset
            },
            concat: {
                value: _this.concat,
                writable: true
            },
            toString: {
                value: _this._toString,
                writable: true
            }
        });
        return _this;
    }
    AVM1ColorTransform.prototype.getRedMultiplier = function () {
        return this._rawData[0];
    };
    AVM1ColorTransform.prototype.setRedMultiplier = function (value) {
        this._rawData[0] = value;
    };
    AVM1ColorTransform.prototype.getGreenMultiplier = function () {
        return this._rawData[1];
    };
    AVM1ColorTransform.prototype.setGreenMultiplier = function (value) {
        this._rawData[1] = value;
    };
    AVM1ColorTransform.prototype.getBlueMultiplier = function () {
        return this._rawData[2];
    };
    AVM1ColorTransform.prototype.setBlueMultiplier = function (value) {
        this._rawData[2] = value;
    };
    AVM1ColorTransform.prototype.getAlphaMultiplier = function () {
        return this._rawData[3];
    };
    AVM1ColorTransform.prototype.setAlphaMultiplier = function (value) {
        this._rawData[3] = value;
    };
    AVM1ColorTransform.prototype.getRedOffset = function () {
        return this._rawData[4];
    };
    AVM1ColorTransform.prototype.setRedOffset = function (value) {
        this._rawData[4] = value;
    };
    AVM1ColorTransform.prototype.getGreenOffset = function () {
        return this._rawData[5];
    };
    AVM1ColorTransform.prototype.setGreenOffset = function (value) {
        this._rawData[5] = value;
    };
    AVM1ColorTransform.prototype.getBlueOffset = function () {
        return this._rawData[6];
    };
    AVM1ColorTransform.prototype.setBlueOffset = function (value) {
        this._rawData[6] = value;
    };
    AVM1ColorTransform.prototype.getAlphaOffset = function () {
        return this._rawData[7];
    };
    AVM1ColorTransform.prototype.setAlphaOffset = function (value) {
        this._rawData[7] = value;
    };
    AVM1ColorTransform.prototype.getRM = function () {
        return this._rawData[0] * 100;
    };
    AVM1ColorTransform.prototype.setRM = function (value) {
        this._rawData[0] = value / 100;
    };
    AVM1ColorTransform.prototype.getGM = function () {
        return this._rawData[1] * 100;
    };
    AVM1ColorTransform.prototype.setGM = function (value) {
        this._rawData[1] = value / 100;
    };
    AVM1ColorTransform.prototype.getBM = function () {
        return this._rawData[2] * 100;
    };
    AVM1ColorTransform.prototype.setBM = function (value) {
        this._rawData[2] = value / 100;
    };
    AVM1ColorTransform.prototype.getAM = function () {
        return this._rawData[3] * 100;
    };
    AVM1ColorTransform.prototype.setAM = function (value) {
        this._rawData[3] = value / 100;
    };
    AVM1ColorTransform.prototype.getRO = function () {
        return this._rawData[4];
    };
    AVM1ColorTransform.prototype.setRO = function (value) {
        this._rawData[4] = value;
    };
    AVM1ColorTransform.prototype.getGO = function () {
        return this._rawData[5];
    };
    AVM1ColorTransform.prototype.setGO = function (value) {
        this._rawData[5] = value;
    };
    AVM1ColorTransform.prototype.getBO = function () {
        return this._rawData[6];
    };
    AVM1ColorTransform.prototype.setBO = function (value) {
        this._rawData[6] = value;
    };
    AVM1ColorTransform.prototype.getAO = function () {
        return this._rawData[7];
    };
    AVM1ColorTransform.prototype.setAO = function (value) {
        this._rawData[7] = value;
    };
    AVM1ColorTransform.prototype.getRgb = function () {
        return ((this._rawData[4] << 16) | (this._rawData[5] << 8) | this._rawData[6]);
    };
    AVM1ColorTransform.prototype.setRgb = function (rgb) {
        var argb = ColorUtils.float32ColorToARGB(rgb);
        this._rawData[4] = argb[1]; //(value >> 16) & 0xFF;
        this._rawData[5] = argb[2]; //(value >> 8) & 0xFF;
        this._rawData[6] = argb[3]; //value & 0xFF;
        this._rawData[0] = 0;
        this._rawData[1] = 0;
        this._rawData[2] = 0;
    };
    AVM1ColorTransform.prototype.concat = function (second) {
        console.warn('AVM1ColorTransform concat is not implemented');
        //this._ctAdaptee.concat(second._ctAdaptee);
    };
    AVM1ColorTransform.prototype._toString = function () {
        return '[object Object]';
    };
    AVM1ColorTransform.fromAwayColorTransform = function (context, t) {
        return new AVM1ColorTransform(context, t.redMultiplier, t.greenMultiplier, t.blueMultiplier, t.alphaMultiplier, t.redOffset, t.greenOffset, t.blueOffset, t.alphaOffset);
    };
    return AVM1ColorTransform;
}(AVM1Object));
export { AVM1ColorTransform };
var AVM1ColorTransformFunction = /** @class */ (function (_super) {
    __extends(AVM1ColorTransformFunction, _super);
    function AVM1ColorTransformFunction(context) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            prototype: {
                value: new AVM1ColorTransformPrototype(context, _this)
            }
        });
        return _this;
    }
    AVM1ColorTransformFunction.prototype.alConstruct = function (args) {
        var obj = Object.create(AVM1ColorTransform.prototype);
        args = args || [];
        AVM1ColorTransform.apply(obj, [this.context].concat(args));
        return obj;
    };
    return AVM1ColorTransformFunction;
}(AVM1Function));
export { AVM1ColorTransformFunction };
var AVM1ColorTransformPrototype = /** @class */ (function (_super) {
    __extends(AVM1ColorTransformPrototype, _super);
    function AVM1ColorTransformPrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            }
        });
        return _this;
    }
    return AVM1ColorTransformPrototype;
}(AVM1Object));
export { AVM1ColorTransformPrototype };
